import type { ReactNode } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import WaveLogo from '@/creator/components/WaveLogo';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import { FAQ_SECTIONS, SUPPORT_EMAIL } from './faqContent';
import '@/creator-app.css';

const LOGIN_URL_TEXT = 'app.wave.me/login';
const LINKABLE_TEXT = /(hello@wave\.me|app\.wave\.me\/login)/;

const renderAnswerText = (text: string): ReactNode[] =>
    text.split(LINKABLE_TEXT).map((part, index) => {
        if (part === SUPPORT_EMAIL) {
            return (
                <a key={index} href={`mailto:${SUPPORT_EMAIL}`}>
                    {part}
                </a>
            );
        }
        if (part === LOGIN_URL_TEXT) {
            return (
                <Link key={index} to="/login">
                    {part}
                </Link>
            );
        }
        return part;
    });

const FaqsPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useWaveAuth();

    const handleHeaderAction = (): void => navigate(isAuthenticated ? '/' : '/login');

    return (
        <div className="landing-page faq-page">
            <header className="landing-header">
                <Link className="faq-page__home" to="/" aria-label="Wave home">
                    <WaveLogo />
                </Link>
                <nav aria-label="Website navigation">
                    <Link to="/">Home</Link>
                    <a href="#getting-help">Get help</a>
                </nav>
                <button type="button" onClick={handleHeaderAction}>
                    <span>{isAuthenticated ? 'Back to Wave' : 'Log in'}</span> <ArrowRight size={15} />
                </button>
            </header>

            <main>
                <section className="faq-hero">
                    <h1>Frequently asked questions</h1>
                    <p>
                        Everything you need to know about campaigns, posts and getting paid on Wave. Can't find an
                        answer? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                    </p>
                </section>

                <nav className="faq-topics" aria-label="FAQ topics">
                    {FAQ_SECTIONS.map((section) => (
                        <a key={section.id} href={`#${section.id}`}>
                            {section.title}
                        </a>
                    ))}
                </nav>

                {FAQ_SECTIONS.map((section) => (
                    <section
                        key={section.id}
                        id={section.id}
                        className="faq-section"
                        aria-labelledby={`${section.id}-title`}
                    >
                        <h2 id={`${section.id}-title`}>{section.title}</h2>
                        <div className="faq-list">
                            {section.faqs.map((faq) => (
                                <details key={faq.question} className="faq-item">
                                    <summary>
                                        <span>{faq.question}</span>
                                        <ChevronDown size={18} aria-hidden />
                                    </summary>
                                    <div className="faq-item__answer">
                                        {faq.answer.map((paragraph) => (
                                            <p key={paragraph}>{renderAnswerText(paragraph)}</p>
                                        ))}
                                    </div>
                                </details>
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            <footer className="landing-footer">
                <WaveLogo compact />
                <p>Wave makes creator campaign work simpler.</p>
            </footer>
        </div>
    );
};

export default FaqsPage;
